import React, { useState, useRef, useCallback, useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import { Code } from '@tiptap/extension-code';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';

export interface EditorProps {
    initialContent?: string;
    onChange?: (content: string) => void;
    readOnly?: boolean;
    className?: string;
}

const MenuBar = ({ editor, setShowCode }: { editor: any, setShowCode: any }) => {
    const [color, setColor] = useState('#000000');
    const [linkUrl, setLinkUrl] = useState('');
    const [showLinkInput, setShowLinkInput] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!editor) return null;

    const buttonClass = (isActive: boolean) =>
        `px-2 py-1 rounded text-sm font-medium ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`;

    const applyCommand = useCallback(
        (command: string) => (e: React.MouseEvent) => {
            e.preventDefault();
            if (editor) {
                editor.chain().focus()[command]().run();
            }
        },
        [editor]
    );

    const applyCommandWithArgs = useCallback(
        (command: string, args: any) => (e: React.MouseEvent) => {
            e.preventDefault();
            if (editor) {
                editor.chain().focus()[command](args).run();
            }
        },
        [editor]
    );

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const imageUrl = event.target?.result;
                if (imageUrl) {
                    editor.chain().focus().setImage({ src: imageUrl as string }).run();
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const addLink = () => {
        if (linkUrl) {
            editor.chain().focus().setLink({ href: linkUrl }).run();
            setLinkUrl('');
            setShowLinkInput(false);
        }
    };

    const insertTable = () => {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    };

    return (
        <div className="flex flex-wrap gap-2 border p-3 mb-4 rounded-md bg-gray-50 shadow-sm items-center">
            <div className="flex gap-2">
                <button onClick={applyCommand('undo')} className={buttonClass(false)} disabled={!editor.can().undo()}>↩</button>
                <button onClick={applyCommand('redo')} className={buttonClass(false)} disabled={!editor.can().redo()}>↪</button>
            </div>
            <div className="flex gap-2">
                <button onClick={applyCommand('toggleBold')} className={buttonClass(editor.isActive('bold'))} disabled={!editor}>B</button>
                <button onClick={applyCommand('toggleItalic')} className={buttonClass(editor.isActive('italic'))} disabled={!editor}>I</button>
                <button onClick={applyCommand('toggleUnderline')} className={buttonClass(editor.isActive('underline'))} disabled={!editor}>U</button>
                <button onClick={applyCommand('toggleStrike')} className={buttonClass(editor.isActive('strike'))} disabled={!editor}>S</button>
                <button onClick={applyCommand('toggleHighlight')} className={buttonClass(editor.isActive('highlight'))} disabled={!editor}>H</button>
                <button onClick={applyCommand('toggleSubscript')} className={buttonClass(editor.isActive('subscript'))} disabled={!editor}>Sub</button>
                <button onClick={applyCommand('toggleSuperscript')} className={buttonClass(editor.isActive('superscript'))} disabled={!editor}>Sup</button>
            </div>
            <div className="flex gap-2">
                <button onClick={applyCommandWithArgs('toggleHeading', { level: 1 })} className={buttonClass(editor.isActive('heading', { level: 1 }))} disabled={!editor}>H1</button>
                <button onClick={applyCommandWithArgs('toggleHeading', { level: 2 })} className={buttonClass(editor.isActive('heading', { level: 2 }))} disabled={!editor}>H2</button>
                <button onClick={applyCommandWithArgs('toggleHeading', { level: 3 })} className={buttonClass(editor.isActive('heading', { level: 3 }))} disabled={!editor}>H3</button>
            </div>
            <div className="flex gap-2">
                <button onClick={applyCommand('toggleBulletList')} className={buttonClass(editor.isActive('bulletList'))} disabled={!editor}>• List</button>
                <button onClick={applyCommand('toggleOrderedList')} className={buttonClass(editor.isActive('orderedList'))} disabled={!editor}>1. List</button>
                <button onClick={applyCommand('toggleTaskList')} className={buttonClass(editor.isActive('taskList'))} disabled={!editor}>☐ List</button>
            </div>
            <div className="flex gap-2">
                <button onClick={applyCommand('toggleBlockquote')} className={buttonClass(editor.isActive('blockquote'))} disabled={!editor}>" "</button>
                <button onClick={applyCommand('toggleCodeBlock')} className={buttonClass(editor.isActive('codeBlock'))} disabled={!editor}>Code</button>
            </div>
            <div className="flex gap-2">
                <button onClick={applyCommandWithArgs('setTextAlign', 'left')} className={buttonClass(editor.isActive({ textAlign: 'left' }))} disabled={!editor}>Left</button>
                <button onClick={applyCommandWithArgs('setTextAlign', 'center')} className={buttonClass(editor.isActive({ textAlign: 'center' }))} disabled={!editor}>Center</button>
                <button onClick={applyCommandWithArgs('setTextAlign', 'right')} className={buttonClass(editor.isActive({ textAlign: 'right' }))} disabled={!editor}>Right</button>
            </div>
            <div className="flex gap-2">
                <button onClick={() => setShowLinkInput(!showLinkInput)} className={buttonClass(editor.isActive('link'))} disabled={!editor}>🔗</button>
                <button onClick={() => fileInputRef.current?.click()} className={buttonClass(false)} disabled={!editor}>🖼️</button>
                <button onClick={insertTable} className={buttonClass(false)} disabled={!editor}>📊</button>
            </div>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={color}
                    onChange={(e) => {
                        setColor(e.target.value);
                        if (editor) {
                            editor.chain().focus().setColor(e.target.value).run();
                        }
                    }}
                    className="w-8 h-8 p-0 border rounded-md cursor-pointer"
                    disabled={!editor}
                />
                <span className="text-sm text-gray-700">Text Color</span>
            </div>
            <button onClick={applyCommand('clearNodes')} className={buttonClass(false)} disabled={!editor}>Clear Format</button>
            <button onClick={() => setShowCode((prev: boolean) => !prev)} className={buttonClass(false)} disabled={!editor}>Edit Code</button>

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
            />

            {showLinkInput && (
                <div className="flex gap-2 items-center">
                    <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="Enter URL"
                        className="px-2 py-1 border rounded-md text-sm"
                    />
                    <button onClick={addLink} className={buttonClass(false)}>Add</button>
                    <button onClick={() => setShowLinkInput(false)} className={buttonClass(false)}>Cancel</button>
                </div>
            )}
        </div>
    );
};

const RichEditor: React.FC<EditorProps> = ({
    initialContent = '<p></p>',
    onChange,
    readOnly = false,
    className = ''
}) => {
    const editorRef = useRef(null);
    const [showCode, setShowCode] = useState(false);
    const [editorInitialized, setEditorInitialized] = useState(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
                codeBlock: {
                    HTMLAttributes: {
                        class: 'rounded-md bg-gray-800 p-5 font-mono text-sm text-gray-100',
                    },
                },
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-blue-500 underline',
                },
            }),
            TextStyle,
            Color,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Code,
            Image,
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableCell,
            TableHeader,
            Highlight,
            Subscript,
            Superscript,
            TaskList,
            TaskItem.configure({
                nested: true,
            }),
        ],
        content: initialContent,
        editable: !readOnly,
        editorProps: {
            attributes: {
                class: `focus:outline-none whitespace-pre-wrap prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto ${className}`,
            },
        },
        onCreate: () => {
            setEditorInitialized(true);
        },
        onUpdate: ({ editor }) => {
            if (onChange) {
                onChange(editor.getHTML());
            }
        },
    });

    useEffect(() => {
        return () => {
            if (editor) {
                editor.destroy();
            }
        };
    }, [editor]);

    return (
        <div className="max-w-4xl mx-auto mt-6 p-6 bg-white border rounded-lg shadow">
            {!readOnly && <MenuBar editor={editor} setShowCode={setShowCode} />}
            {showCode ? (
                <textarea
                    className="w-full min-h-[250px] p-4 border rounded-md font-mono text-sm"
                    value={editor?.getHTML() || ''}
                    onChange={(e) => editor?.commands.setContent(e.target.value)}
                    disabled={!editorInitialized || readOnly}
                />
            ) : (
                <EditorContent
                    ref={editorRef}
                    editor={editor}
                    className="min-h-[250px] p-4 border rounded-md"
                />
            )}
        </div>
    );
};

export default RichEditor;
